import { LinkExternal01 } from '@untitledui/icons';

import { formatCurrency } from '@/utils/currency';

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import { EVENT_TYPES, type InvoicePaymentPayload } from '../types';
import { isValidHttpUrl } from '../utils/url';
import type { EventComponentProps } from './types';

/**
 * Parse the payload's decimal-string amount (e.g. "99.00") at the render
 * boundary. The backend serializes amounts as strings inside the JSON payload
 * (precision-safe); '' means unknown.
 */
function parseAmount(amount: string | undefined): number | null {
  if (amount == null || amount === '') {
    return null;
  }
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : null;
}

export function PaymentEvent({ event }: EventComponentProps) {
  let label: string;
  if (event.event_type_name === EVENT_TYPES.INVOICE_PAID) {
    label = 'Invoice paid';
  } else if (event.event_type_name === EVENT_TYPES.INVOICE_PAYMENT_FAILED) {
    label = 'Invoice payment failed';
  } else {
    label = 'Invoice refunded';
  }

  const payload = event.payload as InvoicePaymentPayload | undefined;

  // Only format as currency when Stripe actually supplied a currency code.
  // Falling back to the org default would render a real amount under the wrong
  // symbol; render the bare number instead so the amount is never mislabeled.
  const amount = parseAmount(payload?.amount);
  let amountText: string | null = null;
  if (amount != null) {
    amountText = payload?.currency
      ? formatCurrency(amount, payload.currency)
      : `${amount}`;
  }

  // Link out to Stripe (the system of record): the hosted invoice page for
  // invoice events, the receipt for charge refunds. Scheme-validated to keep
  // javascript: etc. out of hrefs.
  const rawExternalUrl = payload?.hosted_invoice_url || payload?.receipt_url;
  const externalUrl =
    rawExternalUrl && isValidHttpUrl(rawExternalUrl)
      ? rawExternalUrl
      : undefined;

  const ariaLabel = `${label}${amountText ? ': ' + amountText : ''}`;

  const metaTextClass = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;
  const linkText = payload?.invoice_number
    ? `Invoice ${payload.invoice_number}`
    : 'View in Stripe';

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            {bold(label)}
            {amountText != null && <>: {amountText}</>}
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        {externalUrl ? (
          <a
            href={externalUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-1 text-xs text-fg-brand-primary hover:underline'
          >
            {linkText} <LinkExternal01 className='w-3 h-3' />
          </a>
        ) : (
          payload?.invoice_number && (
            <div className={metaTextClass}>
              Invoice {payload.invoice_number}
            </div>
          )
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
